/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./SerializerAPI',
	'../../../dashboard/layouts/SinglePageLayout',
	'../../../dashboard/layouts/TabLayout',
	'../../../dashboard/model/ModelUtils',
	'text!../../../dashboard/layout/templates/Template1.json',
	'../../../app/nls/StringResources'
], (APIFactory, SerializerAPI, singlePageLayout, tabLayout, ModelUtils, template1Json, stringResources) => {

	/**
	 * @implements {SerializerAPI}
	 */
	class Serializer {
		/**
		 * @param {Object} options
		 * @param {Object} options.features An object of the defined dependencies from the feature contribution json
		 */
		constructor({ features }) {
			this._boardModel = features.internal.getBoardModel();
			this._dashboard = features.API;
			this._api = APIFactory.createAPI(this, [SerializerAPI]);
		}

		getAPI() {
			return this._api;
		}

		destroy() {
			delete this._boardModel;
			delete this._dashboard;
			delete this._api;
		}

		toJSON({ contentIds, dashboardFeatures } = {}) {
			let json;
			if (contentIds && Array.isArray(contentIds) && contentIds.length > 0) {
				json = this._buildDashboardSpecForContents(contentIds);
			} else {
				const canvas = this._dashboard.getFeature('Canvas');
				if (canvas) {
					json = this._boardModel.toJSON();
				} else {
					json = this._boardModel.getInitialSpec();
				}
			}

			ModelUtils.pullPropertiesFromContentModelSpec(json);

			if(dashboardFeatures && Array.isArray(dashboardFeatures) && dashboardFeatures.length > 0) {
				if(json.features) {
					Object.keys(json.features).forEach((feature) => {
						if(dashboardFeatures.indexOf(feature) === -1) {
							delete json.features[feature];
						}
					});
				}
			}
			return json;
		}

		/**
		 * build the minimum dashboard spec which only contains the specified list of contents
		 * For now, we use the template1 (no drop zone) to generate the dashboard spec
		 * TODO: dynamically retrieving the current template of the dashboard instead of hard code the template
		 * @param {Array} contentIds - a list of content ids
		 * @return {Object} spec
		 */
		_buildDashboardSpecForContents(contentIds) {
			// get unique ids
			contentIds = contentIds.filter((v, i) => contentIds.indexOf(v) === i);

			// first check if all the content has same parent
			const canvas = this._dashboard.getFeature('Canvas');
			const contents = contentIds.map(contentId => {
				const content = canvas.getContent(contentId);
				if(!content) {
					throw new Error(`Cannot get the content by id ${contentId}`);
				}
				return content;
			});
			const parent = contents[0].getContainer();
			if(parent && parent.getType() === 'genericPage') {
				for(let i = 1; i < contents.length; i++) {
					const parentId = contents[i].getContainer() ? contents[i].getContainer().getId() : null;
					if(parentId !== parent.getId()) {
						throw new Error('the list of contents must have same parent');
					}
				}
			} else {
				throw new Error('the list of contents must be the children of genericPage');
			}

			// build the spec
			const spec = {
				version: this._boardModel.version,
				content: this._boardModel.getContentModel().toJSON()
			};

			const template1 = JSON.parse(template1Json);
			const genericPage = template1.items[0];
			genericPage.items = genericPage.items.concat(
				contents.map(content => {
					const contentSpec = content.getFeature('Serializer').toJSON();
					return contentSpec;
				})
			);
			//get current dashboard layout type
			const dashboardLayoutType = this._boardModel.layout.type;
			if(dashboardLayoutType === 'tab') {
				const theTabLayout = JSON.parse(JSON.stringify(tabLayout.layout)); // multi tab layout
				theTabLayout.items = [{
					...template1,
					title: stringResources.get('defaultTabTitle', {
						index: 1
					})
				}];
				spec.layout = theTabLayout;
			} else {
				spec.layout = {
					...JSON.parse(JSON.stringify(singlePageLayout.layout)), // single page layout
					...template1
				};
			}
			return spec;
		}
	}
	return Serializer;
});
